// ✅ src/pages/Asset/AssetTransferListing.jsx  (FULL FILE - UPDATED WITH LOCALSTORAGE HEADERS)

import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaPlus,
  FaEye,
  FaChevronDown,
  FaChevronUp,
  FaEdit,
  FaCheckCircle,
  FaInbox,
} from "react-icons/fa";
import AssetLayoutWrapper from "../../components/AssetLayoutWrapper";
import { useNavigate, useLocation } from "react-router-dom";

const API_BASE = "http://nissi-india.com/api/transferListing";

// dd/MM/yyyy format
const fmtDMY = (date) => {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

// safe localStorage getter
const getLS = (key) => {
  try {
    return localStorage.getItem(key) || "";
  } catch {
    return "";
  }
};

const AssetTransferListing = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const navigate = useNavigate();
  const location = useLocation();

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchId = getLS("branchId") || getLS("branchid");
  const branchType = getLS("branchType") || getLS("branchtype");
  const userId = getLS("userId") || getLS("loginuserid");

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  // mode
  const pageid = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const mode = params.get("mode");
    if (mode === "1") return 1;
    if (mode === "2") return 2;
    return 0;
  }, [location.search]);

  const [showFilters, setShowFilters] = useState(true);

  const defaultFilters = () => ({
    fromDate: fmtDMY(new Date(Date.now() - 7 * 86400000)),
    toDate: fmtDMY(new Date()),
    fromLocation: "",
    toLocation: "",
  });

  const [filters, setFilters] = useState(defaultFilters);

  const [branchList, setBranchList] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);

  // pagination
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;

  const totalPages = Math.ceil(records.length / recordsPerPage) || 1;
  const startIndex = (currentPage - 1) * recordsPerPage;
  const currentRecords = records.slice(
    startIndex,
    startIndex + recordsPerPage
  );

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  // ✅ Clear filters back to default 7-day range and reload
  const handleClearFilters = () => {
    setFilters(defaultFilters());
    setTimeout(() => fetchList(), 0);
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisible = 10;
    const max = Math.min(totalPages, maxVisible);
    for (let i = 1; i <= max; i++) pages.push(i);
    if (totalPages > maxVisible) pages.push("...");
    return pages;
  };

  const handleAddNew = () => {
    localStorage.removeItem("EditTransID");
    navigate("/Asset/AssetTransferEntry");
  };

  const handleEditRow = (transferId) => {
    if (!transferId) return toast.error("TransferId missing");
    localStorage.setItem("EditTransID", String(transferId));
    navigate(
      `/Asset/AssetTransferEntry?transferId=${encodeURIComponent(transferId)}`
    );
  };

  const handleApproveRow = (transferId) => {
    if (!transferId) return toast.error("TransferId missing");
    localStorage.setItem("EditTransID", String(transferId));
    navigate(
      `/Asset/AssetTransferReceiving?mode=Approve&transferId=${encodeURIComponent(
        transferId
      )}`
    );
  };

  const handleReceiveRow = (transferId) => {
    if (!transferId) return toast.error("TransferId missing");
    localStorage.setItem("EditTransID", String(transferId));
    navigate(
      `/Asset/AssetTransferReceiving?mode=Receive&transferId=${encodeURIComponent(
        transferId
      )}`
    );
  };

  const fetchBranches = async () => {
    try {
      const res = await axios.get(`${API_BASE}/branches`, {
        headers: commonHeaders,
      });

      if (res.data?.success) {
        setBranchList(res.data.data || []);
      } else {
        toast.error(res.data?.message || "Failed to load branches");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message);
    }
  };

  const fetchList = async () => {
    try {
      setLoading(true);

      const res = await axios.get(`${API_BASE}/list`, {
        headers: commonHeaders,
        params: {
          fromDate: filters.fromDate,
          toDate: filters.toDate,
          fromLocation: filters.fromLocation,
          toLocation: filters.toLocation,
          pageid,
          loginuserid: userId,
          branchtype: branchType,
          branchid: branchId,
        },
      });

      if (res.data?.success) {
        setRecords(res.data.data || []);
        setCurrentPage(1);
      } else {
        setRecords([]);
        toast.error(res.data?.message || "Failed to load data");
      }
    } catch (err) {
      setRecords([]);
      toast.error(err?.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBranches();
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageid, commonHeaders]);

  const titleText =
    pageid === 1
      ? "Asset Transfer Approval"
      : pageid === 2
      ? "Asset Receiving Listing"
      : "Asset Transfer Listing";

  const getBranchId = (b) =>
    b?.fld_Branchid ??
    b?.fld_branchid ??
    b?.branchid ??
    b?.BranchId ??
    b?.id ??
    "";

  const getBranchName = (b) =>
    b?.fld_BranchName ??
    b?.fld_branchName ??
    b?.branchname ??
    b?.BranchName ??
    b?.name ??
    "";

  const canApprove = (status) => {
    const s = String(status || "").toLowerCase();
    return s !== "approved" && s !== "inwarded";
  };

  const canReceive = (status) => {
    const s = String(status || "").toLowerCase();
    return s !== "inwarded";
  };

  return (
    <AssetLayoutWrapper>
      <div
        className={`min-h-screen p-6 ${
          isDark ? "bg-gray-900" : "bg-gray-100"
        }`}
      >
        <HeaderBar
          title={<span className="font-dmserif text-3xl">ASSET TRANSFER</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* ==================== HEADER ==================== */}
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <h1
              className={`text-2xl font-bold ${
                isDark ? "text-white" : "text-gray-900"
              }`}
            >
              {titleText}
            </h1>

            <div className="flex items-center gap-3 flex-wrap">
              {pageid === 0 && (
                <button
                  onClick={handleAddNew}
                  className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
                >
                  <FaPlus />
                  Add New
                </button>
              )}

              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {/* ==================== SEARCH FILTERS ==================== */}
          {showFilters && (
            <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>
                  Search Criteria
                </h3>

                <div className="flex gap-2">
                  <button
                    onClick={handleClearFilters}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    }`}
                  >
                    Clear
                  </button>

                  <button
                    onClick={fetchList}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
                  >
                    <FaEye />
                    View
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    From Date
                  </label>
                  <input
                    type="date"
                    value={filters.fromDate}
                    onChange={(e) =>
                      handleFilterChange("fromDate", e.target.value)
                    }
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                    placeholder="dd/mm/yyyy"
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    To Date
                  </label>
                  <input
                    type="date"
                    value={filters.toDate}
                    onChange={(e) =>
                      handleFilterChange("toDate", e.target.value)
                    }
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                    placeholder="dd/mm/yyyy"
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    From Location
                  </label>
                  <select
                    value={filters.fromLocation}
                    onChange={(e) =>
                      handleFilterChange("fromLocation", e.target.value)
                    }
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {branchList.map((b, idx) => {
                      const id = getBranchId(b);
                      const name = getBranchName(b) || id;
                      return (
                        <option key={idx} value={id}>
                          {name}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    To Location
                  </label>
                  <select
                    value={filters.toLocation}
                    onChange={(e) =>
                      handleFilterChange("toLocation", e.target.value)
                    }
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {branchList.map((b, idx) => {
                      const id = getBranchId(b);
                      const name = getBranchName(b) || id;
                      return (
                        <option key={idx} value={id}>
                          {name}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* ==================== RESULTS TABLE ==================== */}
          <div className="overflow-x-auto rounded-lg border shadow-lg">
            {loading ? (
              <div
                className={`p-16 text-center ${
                  isDark
                    ? "bg-gray-800 text-gray-300"
                    : "bg-gray-50 text-gray-700"
                }`}
              >
                Loading...
              </div>
            ) : records.length === 0 ? (
              <div
                className={`p-16 text-center ${
                  isDark ? "bg-gray-800" : "bg-gray-50"
                }`}
              >
                <div
                  className={`text-lg font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  No Records Found
                </div>
                <div className={`text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  No asset transfer records match the current filter criteria.
                </div>
              </div>
            ) : (
              <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                <thead className={`${tableHeaderClass} text-white`}>
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold">S.No</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Transfer Date</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Asset Type</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Asset IdCodes</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">From Branch</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">To Branch</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold">Approval Status</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold">Actions</th>
                  </tr>
                </thead>

                <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                  {currentRecords.map((r, idx) => {
                    const transferId = r.fld_Asset_Transferid;
                    const transferDate = r.fld_TransferDate
                      ? fmtDMY(r.fld_TransferDate)
                      : "";
                    const assetType = r.fld_AssetTypeName || "";
                    const assetCode = r.fld_idcodeno || "";
                    const fromLoc = r.FromBranch || "";
                    const toLoc = r.ToBranch || "";
                    const status = r.ApprovalStatus || "";

                    return (
                      <tr
                        key={String(transferId || idx)}
                        className={`${
                          idx % 2 === 0
                            ? isDark ? "bg-gray-800" : "bg-white"
                            : isDark ? "bg-gray-750" : "bg-gray-50"
                        } transition-colors ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}
                      >
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {startIndex + idx + 1}
                        </td>
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {transferDate}
                        </td>
                        <td className={`px-6 py-4 text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}>
                          {assetType}
                        </td>
                        <td className={`px-6 py-4 text-sm font-medium ${isDark ? "text-blue-400" : "text-blue-600"}`}>
                          {assetCode}
                        </td>
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {fromLoc}
                        </td>
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {toLoc}
                        </td>
                        <td className="px-6 py-4 text-center text-sm">
                          {status}
                        </td>

                        <td className="px-6 py-4 text-center">
                          <div className="flex justify-center gap-2">
                            {pageid === 0 && (
                              <button
                                onClick={() => handleEditRow(transferId)}
                                className="text-yellow-500 hover:text-yellow-600 text-lg transition"
                                title="Edit"
                              >
                               ✏️
                              </button>
                            )}

                            {pageid === 1 && (
                              <button
                                disabled={!canApprove(status)}
                                onClick={() => handleApproveRow(transferId)}
                                className={`text-lg transition ${
                                  canApprove(status)
                                    ? "text-green-500 hover:text-green-600"
                                    : "opacity-40 cursor-not-allowed text-green-500"
                                }`}
                                title="Approve"
                              >
                                <FaCheckCircle />
                              </button>
                            )}

                            {pageid === 2 && (
                              <button
                                disabled={!canReceive(status)}
                                onClick={() => handleReceiveRow(transferId)}
                                className={`text-lg transition ${
                                  canReceive(status)
                                    ? "text-indigo-500 hover:text-indigo-600"
                                    : "opacity-40 cursor-not-allowed text-indigo-500"
                                }`}
                                title="Receive"
                              >
                                <FaInbox />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* ==================== PAGINATION ==================== */}
          {records.length > 0 && (
            <div className="flex justify-start mt-6">
              <div className="flex gap-1">
                {renderPageNumbers().map((p, idx) => (
                  <button
                    key={idx}
                    onClick={() => p !== "..." && setCurrentPage(p)}
                    disabled={p === "..."}
                    className={`px-3 py-2 text-sm font-medium rounded border transition-colors duration-200 ${
                      p === currentPage
                        ? "bg-blue-600 text-white border-blue-600"
                        : p === "..."
                        ? isDark
                          ? "bg-gray-800 text-gray-400 border-gray-700 cursor-not-allowed"
                          : "bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed"
                        : isDark
                        ? "bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600"
                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          )}
          <ToastContainer
            position="top-right"
            autoClose={2500}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </AssetLayoutWrapper>
  );
};

export default AssetTransferListing;